import { useState } from 'react';
import { clientsApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import {
  BoutonAjoutPersonne,
  NouvellePersonne,
  PastilleFonction,
} from '../clients/PersonnesAContacter.jsx';
import { modifieContacts, voitLesRemises } from '../clients/personnes';
import { tr, trf } from '../../lib/i18n';
import { _inp } from '../../components/ui/Inputs.jsx';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { C } from '../../theme/palette';

/**
 * Une ligne à cocher, comme sur la maquette : case verte, titre, petit texte.
 *
 * @param {object} props propriétés
 * @returns {import('react').ReactElement} la ligne
 */
function Choix({ coche, onClick, titre, detail, pastille, desactive }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={coche}
      disabled={desactive}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        border: '1.5px solid ' + (coche ? C.teal : C.border),
        background: coche ? C.tealL : C.white,
        borderRadius: 12,
        padding: '10px 12px',
        marginBottom: 7,
        cursor: desactive ? 'not-allowed' : 'pointer',
        opacity: desactive ? 0.55 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto',
          width: 20,
          height: 20,
          borderRadius: 6,
          border: '2px solid ' + C.teal,
          background: coche ? C.teal : C.white,
          color: '#fff',
          fontSize: 11,
          fontWeight: 900,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {coche ? '✓' : ''}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
          {titre}
          {pastille}
        </span>
        {detail && (
          <span
            style={{
              display: 'block',
              fontSize: 9.5,
              color: C.muted,
              overflowWrap: 'anywhere',
              marginTop: 2,
            }}
          >
            {detail}
          </span>
        )}
      </span>
    </button>
  );
}

function Etape({ n, texte }) {
  return (
    <div
      style={{
        fontSize: 9.5,
        fontWeight: 900,
        color: C.teal,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        margin: '12px 0 7px',
      }}
    >
      {n} {texte}
    </div>
  );
}

/**
 * « ✉️ Envoyer le catalogue » à plusieurs personnes d'une société cliente,
 * comme sur la maquette approuvée : le catalogue de la société, avec le prix
 * public et ses prix (ses remises article par article, celles de sa fiche),
 * sans choix à faire, les personnes cochées (celles de la fiche, ou ajoutées ici et gardées dans la
 * fiche), le PDF et l'invitation à ouvrir son compte. Chacun reçoit un seul
 * e-mail, depuis l'adresse SwissTech de la société.
 *
 * @param {object} props propriétés
 * @param {object} props.client société cliente
 * @param {boolean} props.ttc prix TVA comprise
 * @param {Function} props.setClients mise à jour des clients (fiche)
 * @param {Function} props.onFermer retour au catalogue
 * @param {string} [props.retour] texte du bouton de retour
 * @returns {import('react').ReactElement} le panneau d'envoi
 */
export function EnvoiCatalogue({ client, ttc, setClients, onFermer, retour }) {
  const contacts = client.contacts || [];
  // L'adresse de la fiche du client, en plus de ses personnes : celle à qui
  // l'on envoie déjà les offres. Elle n'était pas proposée.
  const emailFiche = String(client.email || '').trim();
  const ficheEnPlus =
    !!emailFiche &&
    !contacts.some(
      (p) =>
        String(p.email || '')
          .trim()
          .toLowerCase() === emailFiche.toLowerCase(),
    );
  const [ficheCochee, setFicheCochee] = useState(ficheEnPlus);
  const [coches, setCoches] = useState(
    () =>
      new Set(
        contacts
          .map((p, k) => (p.email && p.fonction !== 'Factures' ? k : -1))
          .filter((k) => k >= 0),
      ),
  );
  const [ajout, setAjout] = useState(false);
  // « remises » : prix public + ses prix ; « base » : sans remise.
  const [prix, setPrix] = useState('remises');
  const [joindrePdf, setJoindrePdf] = useState(true);
  const [inviter, setInviter] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultats, setResultats] = useState(null);

  const choisis = [
    // L'adresse de la société elle-même reçoit le catalogue complet, prix
    // public et ses prix, comme son directeur : elle comptait pour un simple
    // collaborateur, au prix final seulement.
    ...(ficheEnPlus && ficheCochee
      ? [{ nom: client.nom, email: emailFiche, fonction: 'Patron' }]
      : []),
    ...contacts.filter((p, k) => coches.has(k) && p.email),
  ];
  const bascule = (k) =>
    setCoches((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const envoie = async () => {
    if (!choisis.length || enCours) return;
    setEnCours(true);
    setErreur('');
    try {
      const r = await clientsApi.envoieCatalogue(client.id, {
        // Le choix du patron, comme dans l'ancienne version : ses prix
        // (ses remises par article) ou le prix de base, sans remise.
        avecRemises: prix === 'remises',
        ttc,
        joindrePdf,
        inviter,
        destinataires: choisis.map((p) => ({
          nom: p.nom,
          email: p.email,
          fonction: p.fonction || null,
        })),
      });
      setResultats(Array.isArray(r) ? r : []);
    } catch (e) {
      setErreur(e?.message || 'L’envoi n’a pas abouti. Réessaie dans un instant.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <PleinEcran>
      <div
        className="cat-noprint"
        role="dialog"
        aria-label="Envoyer le catalogue"
        style={{
          position: 'fixed',
          inset: 0,
          // Au-dessus du catalogue (9000), sous la visionneuse des PDF (10000).
          zIndex: 9500,
          background: C.bg,
          overflow: 'auto',
          padding: 'calc(14px + var(--st-haut)) 16px calc(30px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <button
            type="button"
            onClick={onFermer}
            style={{
              background: 'none',
              border: 'none',
              color: C.navy,
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
              padding: '8px 0 12px',
            }}
          >
            {retour || '← Retour au catalogue'}
          </button>
          {resultats ? (
            <Card style={{ marginBottom: 11 }}>
              <Bar icon="✓" title="Envoyé" color={C.green} sub={client.nom} />
              <ul
                aria-label="Résultat de l’envoi"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {resultats.map((r, k) => (
                  <li
                    key={k}
                    style={{
                      padding: '10px 13px',
                      borderBottom: '1px solid ' + C.border,
                      fontSize: 10.5,
                      color: C.ink,
                    }}
                  >
                    {r.envoye ? '✅' : '❌'} <b>{r.nom || r.email}</b> —{' '}
                    {r.envoye ? 'envoyé' : 'pas envoyé'}
                    {r.envoye && r.invitation && (
                      <>
                        {' · invitation '}
                        {r.voitRemises ? '(voit les remises)' : '(prix final seulement)'}
                      </>
                    )}
                    {!r.envoye && r.motif && (
                      <div style={{ fontSize: 9.5, color: C.red, marginTop: 2 }}>{r.motif}</div>
                    )}
                  </li>
                ))}
                <li style={{ padding: '10px 13px', fontSize: 9.5, color: C.muted }}>
                  Envoyé depuis ton adresse SwissTech, avec ta signature. Les invitations
                  apparaissent dans la fiche de la société (Compte client).
                </li>
              </ul>
              <div style={{ padding: 13 }}>
                <Btn label="Terminé" full onClick={onFermer} />
              </div>
            </Card>
          ) : (
            <Card style={{ marginBottom: 11 }}>
              <Bar icon="✉️" title={trf('Envoyer le catalogue — {0}', client.nom || '')} />
              <div style={{ padding: '2px 13px 13px' }}>
                <Etape n="①" texte="Catalogue" />
                {/* Avec ou sans ses remises, au choix : « il faut remettre le
                    choix, remise ou sans remise ». */}
                <Choix
                  coche={prix === 'remises'}
                  onClick={() => setPrix('remises')}
                  titre={tr('Prix public + ses prix')}
                  detail={tr('ses remises par article, celles de sa fiche client')}
                />
                <Choix
                  coche={prix === 'base'}
                  onClick={() => setPrix('base')}
                  titre={tr('Sans remise')}
                  detail={tr('prix de base')}
                />

                <Etape n="②" texte="À qui (un seul envoi)" />
                {ficheEnPlus && (
                  <Choix
                    coche={ficheCochee}
                    onClick={() => setFicheCochee((v) => !v)}
                    titre={client.nom}
                    detail={emailFiche + ' · ' + tr('Fiche du client')}
                  />
                )}
                {contacts.length === 0 && !ficheEnPlus && !ajout && (
                  <div style={{ fontSize: 9.5, color: C.muted, fontStyle: 'italic' }}>
                    Aucune personne dans la fiche de la société pour l’instant.
                  </div>
                )}
                {contacts.map((p, k) => (
                  <Choix
                    key={k + p.email}
                    coche={coches.has(k) && !!p.email}
                    desactive={!p.email}
                    onClick={() => bascule(k)}
                    titre={p.nom}
                    pastille={<PastilleFonction fonction={p.fonction} />}
                    detail={p.email || 'Pas d’adresse e-mail'}
                  />
                ))}
                {ajout ? (
                  <NouvellePersonne
                    onAjoute={(p) => {
                      const rang = contacts.length;
                      modifieContacts(setClients, client.id, (l) => [...l, p]);
                      setCoches((s) => new Set(s).add(rang));
                      setAjout(false);
                    }}
                    onAnnule={() => setAjout(false)}
                  />
                ) : (
                  <BoutonAjoutPersonne
                    label="＋ Ajouter une personne (nom, e-mail, fonction)"
                    onClick={() => setAjout(true)}
                  />
                )}
                <div style={{ fontSize: 9, color: C.muted, margin: '6px 0 0' }}>
                  Les adresses restent enregistrées dans la fiche de la société.
                </div>

                <Etape n="③" texte="Avec" />
                <Choix
                  coche={joindrePdf}
                  onClick={() => setJoindrePdf((v) => !v)}
                  titre="Le catalogue en PDF joint"
                />
                <Choix
                  coche={inviter}
                  onClick={() => setInviter((v) => !v)}
                  titre="Une invitation à ouvrir son compte"
                  detail="pour commander en ligne — chacun reçoit son propre lien"
                />
                {inviter && choisis.length > 0 && (
                  <div style={{ fontSize: 9, color: C.muted, margin: '2px 0 4px' }}>
                    {trf(
                      '{0} responsable(s) (voient les remises) · {1} collaborateur(s) (prix final seulement)',
                      choisis.filter((p) => voitLesRemises(p.fonction)).length,
                      choisis.filter((p) => !voitLesRemises(p.fonction)).length,
                    )}
                  </div>
                )}
                {!joindrePdf && !inviter && (
                  <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 4 }}>
                    Coche au moins le PDF ou l’invitation.
                  </div>
                )}
                {erreur && (
                  <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 8 }}>
                    {erreur}
                  </div>
                )}
                <div style={{ marginTop: 12 }}>
                  <Btn
                    label={
                      enCours
                        ? 'Envoi en cours…'
                        : choisis.length === 1
                          ? '✉️ Envoyer à 1 personne'
                          : trf('✉️ Envoyer à {0} personnes', choisis.length)
                    }
                    color={C.green}
                    full
                    disabled={!choisis.length || enCours || (!joindrePdf && !inviter)}
                    onClick={envoie}
                  />
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </PleinEcran>
  );
}

/** Une adresse e-mail lisible : texte@domaine.pays. */
const ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * « ✉️ Envoyer par e-mail » du catalogue neutre : le catalogue au prix public,
 * sans aucune remise, à n'importe quelles adresses — un prospect, un client
 * qui n'a pas de compte. Chacun reçoit un seul e-mail avec le PDF, depuis
 * l'adresse SwissTech de la société.
 *
 * @param {object} props propriétés
 * @param {boolean} props.ttc prix TVA comprise
 * @param {Function} props.onFermer retour au catalogue
 * @returns {import('react').ReactElement} le panneau d'envoi
 */
export function EnvoiCatalogueNeutre({ ttc, onFermer }) {
  const [adresses, setAdresses] = useState([]);
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [resultats, setResultats] = useState(null);

  const ajoute = () => {
    const a = saisie.trim();
    if (!ADRESSE.test(a)) {
      setErreur(tr('L’adresse e-mail est invalide.'));
      return;
    }
    if (!adresses.some((x) => x.toLowerCase() === a.toLowerCase())) setAdresses((l) => [...l, a]);
    setSaisie('');
    setErreur('');
  };

  const envoie = async () => {
    if (!adresses.length || enCours) return;
    setEnCours(true);
    setErreur('');
    try {
      const r = await clientsApi.envoieCatalogueNeutre({
        ttc,
        destinataires: adresses.map((email) => ({ nom: '', email })),
      });
      setResultats(Array.isArray(r) ? r : []);
    } catch (e) {
      setErreur(e?.message || 'L’envoi n’a pas abouti. Réessaie dans un instant.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <PleinEcran>
      <div
        className="cat-noprint"
        role="dialog"
        aria-label={tr('Envoyer le catalogue par e-mail')}
        style={{
          position: 'fixed',
          inset: 0,
          // Au-dessus du catalogue (9000), sous la visionneuse des PDF (10000).
          zIndex: 9500,
          background: C.bg,
          overflow: 'auto',
          padding: 'calc(14px + var(--st-haut)) 16px calc(30px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <button
            type="button"
            onClick={onFermer}
            style={{
              background: 'none',
              border: 'none',
              color: C.navy,
              fontSize: 15,
              fontWeight: 800,
              cursor: 'pointer',
              padding: '8px 0 12px',
            }}
          >
            ← Retour au catalogue
          </button>
          {resultats ? (
            <Card style={{ marginBottom: 11 }}>
              <Bar icon="✓" title="Envoyé" color={C.green} />
              <ul
                aria-label="Résultat de l’envoi"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {resultats.map((r, k) => (
                  <li
                    key={k}
                    style={{
                      padding: '10px 13px',
                      borderBottom: '1px solid ' + C.border,
                      fontSize: 10.5,
                      color: C.ink,
                    }}
                  >
                    {r.envoye ? '✅' : '❌'} <b>{r.email}</b> — {r.envoye ? 'envoyé' : 'pas envoyé'}
                    {!r.envoye && r.motif && (
                      <div style={{ fontSize: 9.5, color: C.red, marginTop: 2 }}>{r.motif}</div>
                    )}
                  </li>
                ))}
              </ul>
              <div style={{ padding: 13 }}>
                <Btn label="Terminé" full onClick={onFermer} />
              </div>
            </Card>
          ) : (
            <Card style={{ marginBottom: 11 }}>
              <Bar icon="✉️" title={tr('Envoyer le catalogue par e-mail')} />
              <div style={{ padding: '10px 13px 13px' }}>
                <div
                  style={{
                    border: '1.5px solid ' + C.teal,
                    background: C.tealL,
                    borderRadius: 12,
                    padding: '10px 12px',
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.ink }}>
                    {tr('Catalogue prix public')}
                  </div>
                  <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>
                    {tr('sans remise — à envoyer à qui tu veux, avec ou sans compte')}
                  </div>
                </div>
                {adresses.map((a) => (
                  <div
                    key={a}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      border: '1px solid ' + C.teal,
                      background: C.tealL,
                      borderRadius: 10,
                      padding: '7px 10px',
                      marginBottom: 6,
                      fontSize: 11,
                    }}
                  >
                    <span style={{ color: C.teal, fontWeight: 900 }}>✓</span>
                    <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{a}</span>
                    <button
                      type="button"
                      aria-label={tr('Retirer') + ' ' + a}
                      onClick={() => setAdresses((l) => l.filter((x) => x !== a))}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: C.red,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="email"
                    aria-label={tr('Adresse e-mail')}
                    value={saisie}
                    placeholder={tr('Adresse e-mail (prospect, client…)')}
                    onChange={(e) => setSaisie(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        ajoute();
                      }
                    }}
                    style={{ ..._inp, flex: 1 }}
                  />
                  <Btn label="＋ Ajouter" color={C.teal} onClick={ajoute} />
                </div>
                {erreur && (
                  <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 6 }}>
                    {erreur}
                  </div>
                )}
                <div style={{ marginTop: 12 }}>
                  <Btn
                    label={
                      enCours
                        ? 'Envoi en cours…'
                        : trf('✉️ Envoyer à {0} adresse(s)', adresses.length)
                    }
                    color={C.green}
                    full
                    disabled={!adresses.length || enCours}
                    onClick={envoie}
                  />
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </PleinEcran>
  );
}
